import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { currentWeek } from "@/lib/week";
import WeekPicker, { OmSelector } from "@/components/WeekPicker";
import WeekritmeRequestDialog, { VerzoekItem } from "@/components/WeekritmeRequestDialog";
import { Check, Sparkles, Repeat, GitPullRequest } from "lucide-react";
import { LoadError, errMessage } from "@/components/LoadError";
import { toast } from "sonner";
import { ToelichtingKnop, ToelichtingRegel } from "@/components/WeekritmeToelichting";
import { useWeekDagen, DagKop } from "@/components/WeekritmeDag";
import { ExtraGedaan, ExtraAndereDag, aantalExtra } from "@/components/WeekritmeExtra";

const DAY_ORDER = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];

const TYPE_BADGE = {
  discipline: { label: "Discipline", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Sparkles },
  rooster: { label: "Rooster", cls: "bg-blue-100 text-blue-800 border-blue-200", icon: Repeat },
};

export default function Weekritme() {
  const { user } = useAuth();
  const [week, setWeek] = useState(currentWeek());
  const isOmIdentity = user.role === "om" || (user.role === "oversight" && user.canDoOm);
  const [om, setOm] = useState(user.username);
  const [oms, setOms] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [profiel, setProfiel] = useState(undefined); // undefined = nog niet geladen
  const [checks, setChecks] = useState({});
  const [toel, setToel] = useState({});
  const [openToel, setOpenToel] = useState({});
  const [toelKey, setToelKey] = useState("");
  const toelSleutel = useRef("");
  toelSleutel.current = `${week}|${om}`;
  const [checksLoaded, setChecksLoaded] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [metaError, setMetaError] = useState("");
  const [checksError, setChecksError] = useState("");

  // Bewerkbaar alleen als je JE EIGEN ritme bekijkt EN een profiel hebt (server weigert anders de toggle).
  const editable = om === user.username && !!profiel && checksLoaded;
  const toelKlaar = checksLoaded && toelKey === `${week}|${om}`;
  const isOm = isOmIdentity;

  const loadMeta = () => api.get("/meta").then((r) => {
      setOms([...(r.data.oms || []), ...(r.data.afdelingLeden || [])]);   // v19-C4: oversight leest ook het ritme van afdelingsleden
      setMetaError("");
    }).catch((e) => setMetaError(errMessage(e)));
  useEffect(() => { loadMeta(); }, [user.role]);

  const loadRequests = () => {
    if (isOm) api.get("/weekritme-requests").then((r) => setMyRequests(r.data)).catch(() => {});
  };
  useEffect(loadRequests, [isOm]);

  const checksSeq = useRef(0);
  const [extra, setExtra] = useState([]);   // v20 B: "Extra gedaan"
  const loadChecks = () => {
    if (!om) return;
    const seq = ++checksSeq.current;
    // Reset zodat een mislukte week/OM-wissel nooit vinkjes/taken van de vorige context toont/schrijft.
    setChecks({});
    setToel({});
    setExtra([]);
    setToelKey("");
    setOpenToel({});
    setTasks([]);
    setProfiel(undefined);
    setChecksLoaded(false);
    // De taken komen mee met de bekeken persoon (hun profiel), NIET uit /meta.
    api.get(`/weekritme?week=${week}&om=${om}`)
      .then((r) => {
        if (seq !== checksSeq.current) return;
        setTasks(r.data.tasks || []);
        setProfiel(r.data.profiel || null);
        setChecks(r.data.checks || {});
        setToel(r.data.toelichtingen || {});
        setExtra(r.data.extra || []);
        setToelKey(`${week}|${om}`);
        setChecksLoaded(true);
        setChecksError("");
      })
      .catch((e) => { if (seq === checksSeq.current) setChecksError(errMessage(e)); });
  };
  useEffect(loadChecks, [week, om]);

  // A4: compact overzicht van het bedrijfsleider-ritme per locatie — read-only.
  const [blScores, setBlScores] = useState([]);
  const [blScoresError, setBlScoresError] = useState("");
  const loadBlScores = () => {
    api.get(`/weekritme/bl-scores?week=${week}`)
      .then((r) => { setBlScores(r.data); setBlScoresError(""); })
      .catch((e) => { setBlScores([]); setBlScoresError(errMessage(e)); });
  };
  useEffect(loadBlScores, [week]);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of tasks) {
      (g[t.day] = g[t.day] || []).push(t);
    }
    return g;
  }, [tasks]);

  const dagen = DAY_ORDER.filter((d) => grouped[d]);
  const wd = useWeekDagen({ week, sleutel: `${week}|${om}`, dagen, grouped, checks, klaar: checksLoaded });
  const doneCount = tasks.filter((t) => checks[t.id]).length;
  const pct = Math.round((doneCount / (tasks.length || 1)) * 100);

  const toggle = async (taskId) => {
    if (!editable) return;
    const next = !checks[taskId];
    setChecks((c) => ({ ...c, [taskId]: next }));
    try {
      await api.post("/weekritme/toggle", { week, om, taskId, done: next });
    } catch (e) {
      setChecks((c) => ({ ...c, [taskId]: !next }));
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
  };

  return (
    <div data-testid="weekritme-page" className="pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 md:mb-4">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Weekritme</h1>
          <p className="text-sm text-zinc-500">Dagelijkse taken, per OM afgevinkt. Reset per week.</p>
          {isOm && <p data-testid="weekritme-voorstel-uitleg" className="text-xs text-zinc-500 mt-1 max-w-xl">Taak voorstellen gaat alleen over de taken in je weekritme. Gaat het over de app zelf, een fout of een idee? Gebruik het wolkje rechtsonder.</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isOm && (
            <button data-testid="weekritme-propose-btn" onClick={() => setReqOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-400 transition-colors">
              <GitPullRequest className="h-4 w-4" /> Taak voorstellen
            </button>
          )}
          {user.role === "oversight" && <OmSelector om={om} setOm={setOm} oms={oms} selfUsername={user.username} />}
          <WeekPicker week={week} setWeek={setWeek} />
        </div>
      </div>

      {(metaError || checksError || blScoresError) && (
        <div className="mb-4 space-y-2">
          <LoadError error={metaError} onRetry={loadMeta} testid="weekritme-meta-error" />
          <LoadError error={checksError} onRetry={loadChecks} testid="weekritme-load-error" />
          <LoadError error={blScoresError} onRetry={loadBlScores} testid="weekritme-blscores-error" />
        </div>
      )}

      {checksLoaded && profiel === null && !checksError && (
        <div data-testid="weekritme-no-profile" className="rounded-md bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-800">
          Er is nog geen weekritme-profiel toegewezen aan {om === user.username ? "je account" : om}. {om === user.username ? "Vraag je oversight dit in te stellen." : "Oversight stelt dit in via Instellingen."}
        </div>
      )}

      {(!checksLoaded || profiel) && (<>
      <div className="flex items-center gap-4 mb-3 md:mb-4 rounded-[10px] bg-white border border-zinc-200 px-4 py-3">
        <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">
          Voortgang <span className="capitalize text-zinc-400">· {om}</span>
        </div>
        <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div data-testid="weekritme-progress" className="font-mono text-lg font-black text-zinc-900 tabular-nums">
          {doneCount}<span className="text-zinc-300">/{tasks.length}</span>
        </div>
      </div>

      {!editable && (
        <p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3">Alleen-lezen weergave</p>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {DAY_ORDER.filter((d) => grouped[d] || (toelKlaar && aantalExtra(extra, d) > 0)).map((day) => (
          <div key={day} className="bg-white rounded-[10px] border border-zinc-200 overflow-hidden hover:border-zinc-300 transition-colors">
            <DagKop dag={day} taken={grouped[day] || []} checks={checks} smal={wd.smal} open={wd.isOpen(day)} onToggle={() => wd.toggle(day)} voorbij={wd.voorbij(day)} prefix="weekritme" extra={toelKlaar ? aantalExtra(extra, day) : 0} />
            <div data-testid={`dag-taken-${day}`} className={`divide-y divide-zinc-200 ${wd.isOpen(day) ? "" : "hidden"}`}>
              {(grouped[day] || []).map((t) => {
                const badge = TYPE_BADGE[t.type];
                const done = !!checks[t.id];
                return (
                  <div key={t.id}>
                  <div className="flex items-start">
                  <button
                    data-testid={`weekritme-task-${t.id}`}
                    onClick={() => toggle(t.id)}
                    disabled={!editable}
                    className={`flex-1 min-w-0 text-left flex items-start gap-3 px-4 py-3.5 transition-colors ${editable ? "hover:bg-zinc-50 cursor-pointer active:bg-zinc-100" : "cursor-default"}`}
                  >
                    <span className={`mt-0.5 h-6 w-6 sm:h-5 sm:w-5 shrink-0 rounded-sm border flex items-center justify-center transition-colors ${done ? "bg-emerald-500 border-emerald-500" : "border-zinc-300 bg-white"}`}>
                      {done && <Check className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className="flex-1">
                      <span className={`text-sm ${done ? "text-zinc-400 line-through" : "text-zinc-700"}`}>{t.text}</span>
                      {badge && (
                        <span className={`ml-2 inline-flex items-center gap-1 align-middle text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${badge.cls}`}>
                          <badge.icon className="h-2.5 w-2.5" /> {badge.label}
                        </span>
                      )}
                    </span>
                  </button>
                  {editable && toelKlaar && <ToelichtingKnop taskId={t.id} open={!!openToel[t.id]} heeft={!!toel[t.id]} onClick={() => setOpenToel((o) => ({ ...o, [t.id]: !o[t.id] }))} />}
                  </div>
                  {toelKlaar && <ToelichtingRegel key={`${week}|${om}|${t.id}`} week={week} om={om} taskId={t.id} waarde={toel[t.id] || ""} editable={editable} open={!!openToel[t.id]}
                    onSaved={(id, v, w, o) => { if (`${w}|${o}` === toelSleutel.current) setToel((m) => ({ ...m, [id]: v })); }} />}
                  </div>
                );
              })}
              {toelKlaar && <ExtraGedaan dag={day} week={week} items={extra} eigen={editable}
                onAdd={(x) => { if (`${x.week}|${x.om}` === toelSleutel.current) setExtra((l) => [...l, x]); }}
                onDelete={(id) => setExtra((l) => l.filter((y) => y.id !== id))} />}
            </div>
          </div>
        ))}
      </div>
      {toelKlaar && editable && <ExtraAndereDag week={week} onAdd={(x) => { if (`${x.week}|${x.om}` === toelSleutel.current) setExtra((l) => [...l, x]); }} />}
      </>)}

      {blScores.length > 0 && (
        <div className="mt-8" data-testid="weekritme-bl-scores">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
            <Check className="h-4 w-4" /> Bedrijfsleider-ritme deze week
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {blScores.map((s, i) => {
              const compleet = s.total > 0 && s.done >= s.total;
              const stil = s.total > 0 && s.done === 0;
              // v19-N4: twee accounts op één locatie → eigen key, gebruikersnaam erbij; eerste rij houdt de oude testid
              const meer = blScores.filter((x) => x.locatie === s.locatie).length > 1;
              const eerste = blScores.findIndex((x) => x.locatie === s.locatie) === i;
              return (
                <div key={s.username} data-testid={eerste ? `bl-score-${s.locatie}` : `bl-score-${s.locatie}-${s.username}`}
                  className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${stil ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`}>
                  <span className="font-semibold text-sm text-zinc-800">{s.locatie}{meer && <span className="font-normal text-xs text-zinc-400"> · {s.username}</span>}</span>
                  {s.total > 0 ? (
                    <span className={`font-mono text-sm font-bold tabular-nums ${compleet ? "text-emerald-600" : stil ? "text-amber-700" : "text-blue-700"}`}>
                      {s.done}/{s.total}
                    </span>
                  ) : (
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">niet ingericht</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isOm && myRequests.length > 0 && (
        <div className="mt-8" data-testid="weekritme-my-requests">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
            <GitPullRequest className="h-4 w-4" /> Mijn voorstellen
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {myRequests.map((r) => <VerzoekItem key={r.id} req={r} testid={`my-request-${r.id}`} />)}
          </div>
        </div>
      )}

      <WeekritmeRequestDialog open={reqOpen} onOpenChange={setReqOpen} tasks={tasks} onSubmitted={loadRequests} />
    </div>
  );
}
